import proj4 from "proj4";

export const WGS84_GPS_CRS = {
  datum: "WGS84",
  coordinateType: "geographic",
  epsg: "EPSG:4326",
  name: "WGS 84",
  source: "mobile-gps",
};

export const MOBILE_GPS_MAPPING = "mobile_gps_mapping";
export const MANUAL_IMPORTATION = "manual_importation";
export const WGS84_DATUM = "WGS84";
export const ARC1960_DATUM = "ARC1960";

export const ARC1960_GEOGRAPHIC_CRS = {
  datum: ARC1960_DATUM,
  coordinateType: "geographic",
  epsg: "EPSG:4210",
  name: "Arc 1960",
};

const ARC1960_TANZANIA_TRANSFORM = "ARC1960_TANZANIA_EPSG1285";

proj4.defs(
  ARC1960_TANZANIA_TRANSFORM,
  "+proj=longlat +a=6378249.145 +rf=293.465 +towgs84=-175,-23,-303 +no_defs +type=crs"
);

function getValidPoints(points) {
  return (points || [])
    .map((point) => ({
      lat: Number(point.lat),
      lng: Number(point.lng),
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
}

export function getUtmZoneFromLongitude(longitude) {
  return Math.min(60, Math.max(1, Math.floor((Number(longitude) + 180) / 6) + 1));
}

export function createPointsCrsMetadata({
  mappingMethod = MOBILE_GPS_MAPPING,
  sourceDatum = WGS84_DATUM,
  originalUtmZone = "",
  sourceReference = "",
} = {}) {
  const isArc1960 = sourceDatum === ARC1960_DATUM;

  return {
    sourceDatum: isArc1960 ? ARC1960_DATUM : WGS84_DATUM,
    sourceEpsg: isArc1960 ? ARC1960_GEOGRAPHIC_CRS.epsg : WGS84_GPS_CRS.epsg,
    sourceCoordinateType: "geographic",
    inputFormat: "decimal_degrees",
    axisOrder: "lat_lng",
    units: "degrees",
    originalUtmZone: originalUtmZone || null,
    normalizedEpsg: WGS84_GPS_CRS.epsg,
    transformationEpsg: isArc1960 ? "EPSG:1285" : null,
    sourceReference: sourceReference.trim() || (mappingMethod === MOBILE_GPS_MAPPING ? "mobile_gps" : null),
  };
}

export function normalizeGeographicPointsToWgs84(points, sourceDatum = WGS84_DATUM) {
  const validPoints = getValidPoints(points);

  if (sourceDatum === WGS84_DATUM) {
    return validPoints;
  }

  if (sourceDatum === ARC1960_DATUM) {
    return validPoints.map((point) => {
      const [lng, lat] = proj4(
        ARC1960_TANZANIA_TRANSFORM,
        WGS84_GPS_CRS.epsg,
        [point.lng, point.lat]
      );

      return {
        lat: Number(lat.toFixed(7)),
        lng: Number(lng.toFixed(7)),
      };
    });
  }

  throw new Error("Unsupported coordinate datum");
}

export function getWgs84UtmCrs(points) {
  const validPoints = getValidPoints(points);
  if (validPoints.length === 0) {
    return null;
  }

  const center = validPoints.reduce((result, point) => ({
    lat: result.lat + point.lat / validPoints.length,
    lng: result.lng + point.lng / validPoints.length,
  }), { lat: 0, lng: 0 });

  const zone = getUtmZoneFromLongitude(center.lng);
  const hemisphere = center.lat >= 0 ? "N" : "S";
  const epsgNumber = Number(`${hemisphere === "N" ? "326" : "327"}${String(zone).padStart(2, "0")}`);

  return {
    datum: "WGS84",
    coordinateType: "projected",
    zone,
    hemisphere,
    epsg: `EPSG:${epsgNumber}`,
    name: `WGS 84 / UTM zone ${zone}${hemisphere}`,
    unit: "meter",
  };
}

export function convertWgs84PointsToUtm(points, sourceCrs = WGS84_GPS_CRS) {
  if (sourceCrs.datum !== WGS84_GPS_CRS.datum || sourceCrs.epsg !== WGS84_GPS_CRS.epsg) {
    throw new Error("Only WGS 84 GPS coordinates are supported by this conversion path");
  }

  const validPoints = getValidPoints(points);
  const targetCrs = getWgs84UtmCrs(validPoints);
  if (!targetCrs) {
    return null;
  }

  const projectedPoints = validPoints.map((point) => {
    const [easting, northing] = proj4(
      sourceCrs.epsg,
      targetCrs.epsg,
      [point.lng, point.lat]
    );

    return {
      ...point,
      easting,
      northing,
    };
  });

  return {
    sourceCrs,
    targetCrs,
    points: projectedPoints,
  };
}

export function formatProjectedCoordinate(value) {
  return Number(value).toFixed(3);
}
